import React from "react";
import Figure from "./Figure";
import Nature from "./Nature";

// What a figure is, where it stands, and what made it — everything the hover
// card shows, in full, plus the things a card has no room for.
//
// It is the ANSWER to a click, so it stays mounted once anything has been
// picked: a panel that unmounts when you pick something else would throw focus
// to <body> between selections. Nothing is picked to begin with, and then it
// says so rather than rendering an empty frame.
//
// The first twelve places also carry the house and Turner's exact judgment.
// Witnesses and Judge are not assigned a thirteenth, fourteenth or fifteenth
// house: the panel says where the house sequence ends instead.
export default function Detail({ cell, figure, figures, addition }) {
  if (!cell) {
    return (
      <div className="geo-detail-panel geo-detail-panel--empty" data-testid="geo-detail">
        <p>
          Choose any figure on the shield to see what the 1655 printing says it is, and
          what it was made from.
        </p>
      </div>
    );
  }

  return (
    <div className="geo-detail-panel" data-testid="geo-detail" data-figure={figure.slug}>
      <div className="geo-detail-panel__head">
        <Figure figure={figure} size={68} />
        <div>
          <h3 className="geo-detail-panel__name" data-testid="geo-detail-name">
            {figure.printedName}
          </h3>
          <p className="geo-detail-panel__role">
            {cell.role} on this shield
            {cell.house ? ` · House ${cell.house.number}, ${cell.house.name}` : ""}
            {cell.cast ? " — one of the four that were cast" : ""}
          </p>
          <dl className="geo-detail-panel__facts">
            <div>
              <dt>Dots</dt>
              <dd>
                {figure.dots} — {figure.even ? "even" : "odd"}
              </dd>
            </div>
            <div>
              <dt>Planet</dt>
              <dd>
                {figure.planet ? (
                  <>
                    <span className="geo-detail__planet-symbol" aria-hidden="true">
                      {figure.planetSymbol}
                    </span>{" "}
                    {figure.planet}{" "}
                    <span className="geo-detail__printed">printed “{figure.planetPrinted}”</span>
                  </>
                ) : (
                  "none — the printing gives the nodes no planet of their own"
                )}
              </dd>
            </div>
            <div>
              <dt>Can it judge?</dt>
              <dd data-testid="geo-detail-judge">
                {figure.even
                  ? "Yes. A Judge always has an even number of dots, and this figure does."
                  : `No. A Judge always has an even number of dots, and this figure has ${figure.dots}.`}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {cell.sources.length > 0 ? (
        <div className="geo-detail-panel__made" data-testid="geo-detail-made">
          <h4>What made it</h4>
          <ol className="geo-walk__sources">
            {cell.sources.map((source) => (
              <li key={source.note}>
                <Figure figure={figures[source.figure]} size={32} />
                <span className="geo-walk__source-note">{source.note}</span>
                <span className="geo-walk__source-name">{figures[source.figure].printedName}</span>
              </li>
            ))}
          </ol>
          {addition ? <AdditionTable lines={addition} sources={cell.sources} /> : null}
        </div>
      ) : (
        <p className="geo-detail-panel__cast">
          This figure was cast directly from four rows of uncounted points. Each row leaves
          one dot when its total is odd and two when its total is even.
        </p>
      )}

      <Nature nature={figure.nature} />

      {cell.house ? (
        <section className="geo-detail-panel__house" data-testid="geo-detail-house">
          <h4>
            In House {cell.house.number}: {cell.house.name}
          </h4>
          <p className="geo-detail-panel__topic">{cell.house.topic?.quote}</p>
          <blockquote>{cell.house.judgment.quote}</blockquote>
          <p className="geo-detail-panel__source">
            {cell.house.judgment.locator} · Turner, 1655
          </p>
          {cell.house.judgment.note ? (
            <p className="geo-detail-panel__house-note">{cell.house.judgment.note}</p>
          ) : null}
          <p className="geo-detail-panel__house-links">
            <a href={cell.house.path}>About {cell.house.name}</a>
            {" · "}
            <a href={cell.house.meaningsPath}>All twelve house topics</a>
          </p>
        </section>
      ) : (
        <p className="geo-detail-panel__absent" data-testid="geo-detail-no-house">
          <strong>{cell.role} is not one of the twelve houses.</strong> Houses 1–4 are the
          Mothers, 5–8 the Daughters, and 9–12 the Nieces. The Witnesses and Judge follow
          them as parts of the judgment, not as extra houses.
        </p>
      )}

      <p className="geo-detail-panel__more">
        <a href={figure.path}>Everything on {figure.printedName} →</a>
      </p>
    </div>
  );
}

function AdditionTable({ lines, sources }) {
  return (
    <table className="geo-add" data-testid="geo-detail-add">
      <caption className="visually-hidden">
        {sources[0].note} added to {sources[1].note}, line by line
      </caption>
      <thead>
        <tr>
          <th scope="col">
            <span className="visually-hidden">Line</span>
          </th>
          <th scope="col">{sources[0].note}</th>
          <th scope="col">{sources[1].note}</th>
          <th scope="col">Points</th>
          <th scope="col">Leaves</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => (
          <tr key={index}>
            <th scope="row" className="geo-add__line">
              {index + 1}
            </th>
            <td>
              <span className="geo-add__dots">{"·".repeat(line.first)}</span>
            </td>
            <td>
              <span className="geo-add__dots">{"·".repeat(line.second)}</span>
            </td>
            <td>
              {line.total} — <span className="geo-add__parity">{line.total % 2 === 0 ? "even" : "odd"}</span>
            </td>
            <td>
              <span className="geo-add__dots geo-add__dots--result">{"·".repeat(line.dots)}</span>
              <span className="geo-add__result-word">{line.dots === 2 ? "two" : "one"}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
